import React, { useState } from 'react';
import { App, Button, Card, Col, Descriptions, Dropdown, Form, Input, InputNumber, List, Modal, Row, Select, Space, Spin, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, CheckOutlined, EditOutlined, FormOutlined, MoreOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import ReasonPrompt from './shared/ReasonPrompt';
import { fmtCount, fmtDateTime, parseJsonArray, RECOMMENDATIONS, recruitErrorMessage, stageLabel, useRecruitAccess } from './shared/recruitUi';

const { Text, Paragraph } = Typography;

const recLabel = (v?: string) => RECOMMENDATIONS.find((r) => r.value === v);

const InterviewDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { canManage } = useRecruitAccess();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeForm] = Form.useForm();

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.interview(id || ''),
    queryFn: () => hrmsService.getInterview(id!),
    enabled: Boolean(id),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.interview(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.interviews() });
    if (row?.application_id) await queryClient.invalidateQueries({ queryKey: hrmsKeys.application(row.application_id) });
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Interview" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Interview" />}>
        <Card>Not found, or you are not on this interview's panel.</Card>
      </WorkspaceLayout>
    );
  }

  const open = ['Scheduled', 'Rescheduled'].includes(row.status);
  const canFeedback = !['Cancelled', 'NoShow'].includes(row.status);
  const feedback: any[] = row.feedback || [];
  const submitted = feedback.filter((f) => f.submitted_at);
  const questions: any[] = row.criteria?.questions || [];
  const panel: any[] = row.panel || [];
  const pendingPanel = panel.filter((p) => !submitted.some((f) => f.interviewer_employee_id === p.id));

  const noShow = () =>
    modal.confirm({
      title: 'Mark as no-show?',
      content: 'The candidate did not attend. The round is closed without feedback.',
      okText: 'Mark no-show',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await hrmsService.noShowInterview(id!);
          message.success('Marked as no-show');
          await invalidate();
        } catch (e: any) {
          message.error(recruitErrorMessage(e));
        }
      },
    });

  const complete = async () => {
    const v = await completeForm.validateFields();
    try {
      await hrmsService.completeInterview(id!, v);
      message.success('Interview completed');
      setCompleteOpen(false);
      await invalidate();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Interview"
          documentNumber={`${row.application_number} · Round ${row.round_number}`}
          subtitle={
            <Text type="secondary">
              {row.candidate_name} — {row.job_title}
            </Text>
          }
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{stageLabel(row.status)}</Tag>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Back
              </Button>
              {canFeedback && (
                <Button icon={<FormOutlined />} type={canManage ? 'default' : 'primary'} onClick={() => navigate(`/hr/interviews/${id}/scorecard`)}>
                  My feedback
                </Button>
              )}
              {canManage && open && (
                <>
                  <Button icon={<EditOutlined />} onClick={() => navigate(`/hr/interviews/${id}/edit`)}>
                    Reschedule / edit
                  </Button>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    onClick={() => {
                      const best = submitted.length
                        ? [...submitted].sort(
                            (a, b) => RECOMMENDATIONS.findIndex((r) => r.value === a.recommendation) - RECOMMENDATIONS.findIndex((r) => r.value === b.recommendation)
                          )[Math.floor(submitted.length / 2)]?.recommendation
                        : undefined;
                      completeForm.setFieldsValue({ recommendation: best, overall_score: row.overall_score != null ? Number(row.overall_score) : undefined });
                      setCompleteOpen(true);
                    }}
                  >
                    Complete round
                  </Button>
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'noshow', label: 'Mark no-show' },
                        { key: 'cancel', label: 'Cancel interview', danger: true },
                      ],
                      onClick: ({ key }) => (key === 'noshow' ? noShow() : setCancelOpen(true)),
                    }}
                  >
                    <Button icon={<MoreOutlined />} />
                  </Dropdown>
                </>
              )}
            </Space>
          }
        />
      }
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card size="small" title={`Panel feedback (${submitted.length}/${panel.length})`} style={{ marginBottom: 16 }}>
            {submitted.length === 0 ? (
              <Text type="secondary">No feedback submitted yet.</Text>
            ) : (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={submitted}
                expandable={{
                  expandedRowRender: (f: any) => {
                    const ratings: any[] = parseJsonArray(f.ratings_json);
                    return (
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Table
                          rowKey="code"
                          size="small"
                          pagination={false}
                          dataSource={ratings}
                          columns={[
                            { title: 'Criterion', dataIndex: 'code', render: (c) => questions.find((q) => q.code === c)?.text || c },
                            {
                              title: 'Rating',
                              dataIndex: 'rating',
                              width: 90,
                              render: (r, x: any) => `${r}/${questions.find((q) => q.code === x.code)?.max_rating || 5}`,
                            },
                            { title: 'Comment', dataIndex: 'comment' },
                          ]}
                        />
                        {f.comments && <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{f.comments}</Paragraph>}
                      </Space>
                    );
                  },
                }}
                columns={[
                  { title: 'Interviewer', dataIndex: 'interviewer_name' },
                  { title: 'Score', dataIndex: 'overall_score', width: 90, render: (v) => (v != null ? `${fmtCount(v, 2)}/5` : '—') },
                  {
                    title: 'Recommendation',
                    dataIndex: 'recommendation',
                    width: 140,
                    render: (v) => {
                      const r = recLabel(v);
                      return r ? <Tag color={r.color}>{r.label}</Tag> : v;
                    },
                  },
                  { title: 'Submitted', dataIndex: 'submitted_at', width: 160, render: (v) => fmtDateTime(v) },
                ]}
              />
            )}
            {pendingPanel.length > 0 && open && (
              <div style={{ marginTop: 12 }}>
                <Text type="secondary">Waiting for: </Text>
                {pendingPanel.map((p) => (
                  <Tag key={p.id}>{p.full_name}</Tag>
                ))}
              </div>
            )}
          </Card>
          {(row.status === 'Completed' || row.feedback) && (
            <Card size="small" title="Panel decision" style={{ marginBottom: 16 }}>
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Recommendation">
                  {recLabel(row.recommendation) ? <Tag color={recLabel(row.recommendation)!.color}>{recLabel(row.recommendation)!.label}</Tag> : row.recommendation || '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Score">{row.overall_score != null ? `${fmtCount(row.overall_score, 2)}/5` : '—'}</Descriptions.Item>
                <Descriptions.Item label="Summary" span={2}>
                  <span style={{ whiteSpace: 'pre-wrap' }}>{row.feedback || '—'}</span>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          )}
          <Card size="small" title={`Criteria — ${row.criteria?.template_name || 'default'}`}>
            <List
              size="small"
              dataSource={questions}
              renderItem={(q: any) => (
                <List.Item extra={<Text type="secondary">weight {q.weight ?? 1}</Text>}>
                  {q.section ? <Tag>{q.section}</Tag> : null}
                  {q.text}
                </List.Item>
              )}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card size="small" title="Details" style={{ marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Candidate">
                {canManage ? <a onClick={() => navigate(`/hr/candidates/${row.candidate_id}`)}>{row.candidate_name}</a> : row.candidate_name}
              </Descriptions.Item>
              <Descriptions.Item label="Application">
                {canManage ? <a onClick={() => navigate(`/hr/applications/${row.application_id}`)}>{row.application_number}</a> : row.application_number}
              </Descriptions.Item>
              <Descriptions.Item label="Job">{row.job_title}</Descriptions.Item>
              <Descriptions.Item label="When">{fmtDateTime(row.scheduled_at)}</Descriptions.Item>
              <Descriptions.Item label="Duration">{row.duration_minutes || 60} min</Descriptions.Item>
              <Descriptions.Item label="Mode">{row.mode}</Descriptions.Item>
              {row.meeting_link && (
                <Descriptions.Item label="Link">
                  <a href={row.meeting_link} target="_blank" rel="noreferrer">
                    Join meeting
                  </a>
                </Descriptions.Item>
              )}
              {row.location_text && <Descriptions.Item label="Location">{row.location_text}</Descriptions.Item>}
              {row.cancel_reason && <Descriptions.Item label="Cancel reason">{row.cancel_reason}</Descriptions.Item>}
            </Descriptions>
          </Card>
          <Card size="small" title="Panel">
            <List size="small" dataSource={panel} renderItem={(p: any) => <List.Item>{p.full_name}{p.department_name ? <Text type="secondary"> · {p.department_name}</Text> : null}</List.Item>} />
          </Card>
        </Col>
      </Row>

      <ReasonPrompt
        open={cancelOpen}
        title="Cancel interview"
        okText="Cancel interview"
        notesRequired
        notesLabel="Reason"
        onCancel={() => setCancelOpen(false)}
        onSubmit={async (v) => {
          try {
            await hrmsService.cancelInterview(id!, { reason: v.notes || '', notify_candidate: true });
            message.success('Interview cancelled — candidate and panel notified');
            setCancelOpen(false);
            await invalidate();
          } catch (e: any) {
            message.error(recruitErrorMessage(e));
          }
        }}
      />

      <Modal open={completeOpen} title="Complete interview round" okText="Complete" onOk={complete} onCancel={() => setCompleteOpen(false)} destroyOnClose>
        <Form form={completeForm} layout="vertical">
          <Form.Item name="recommendation" label="Panel recommendation" rules={[{ required: true, message: 'Choose the recommendation' }]}>
            <Select options={RECOMMENDATIONS.map((r) => ({ value: r.value, label: r.label }))} />
          </Form.Item>
          <Form.Item name="overall_score" label="Overall score (1–5)" extra="Defaults to the average of submitted panel scores">
            <InputNumber min={0} max={5} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="feedback" label="Summary">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </WorkspaceLayout>
  );
};

export default InterviewDetailPage;
